import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { useCounts } from '../../contexts/counts-context';
import { useLogout } from '../../components/logout/logout';
import { MessageIcon } from '../../components/shared-components/message-icon';
import "../../hs-theme.css";
import "./user-panel.css";
import { ICON_PATHS } from '../../components/shared-components/icon-paths';

const ICONS = {
    lock: 'M6 11h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1ZM8 11V7a4 4 0 0 1 8 0v4',
    user: ICON_PATHS.user,
    active: 'M9 11l3 3 8-8M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9',
    inactive: 'M9 3h6l1 2h3a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h3ZM9 12h6M9 16h4',
    folder: 'M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z',
    home: ICON_PATHS.home,
    heart: 'M12 20.5s-7.5-4.6-9.3-9.3C1.4 7.8 3.6 4.5 7 4.5c2 0 3.6 1.1 5 3 1.4-1.9 3-3 5-3 3.4 0 5.6 3.3 4.3 6.7-1.8 4.7-9.3 9.3-9.3 9.3Z',
    phone: 'M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2Z',
    mail: ICON_PATHS.mail,
    bell: 'M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
    write: 'M4 4h16v12H5.2L4 17.2ZM8 8h8M8 12h5',
    credit: 'M3 7h18v10H3ZM3 11h18M7 15h3',
    chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
    logout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
};

export const PanelIcon = ({ name, size = 17 }) => name === 'msg' ? <MessageIcon size={size + 2} /> : (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d={ICONS[name]} /></svg>
);

// `count` names the counter shown next to the item, `alert` highlights it when it is not zero
export const PANEL_MENU = [
    { title: 'my_data', path: '/korisnik/podaci', icon: 'user' },
    { title: 'security', path: '/korisnik/sigurnost', icon: 'lock' },
    { title: 'active', path: '/korisnik/oglasi', icon: 'active' },
    { title: 'non_active', path: '/korisnik/neaktivni-oglasi', icon: 'inactive' },
    { title: 'messages', path: '/korisnik/poruke', icon: 'msg', count: 'messages', alert: true },
    { title: 'following', path: '/korisnik/praceni', icon: 'heart', count: 'following' },
    { title: 'notifications', path: '/korisnik/obavestenja', icon: 'bell', count: 'notifications', alert: true },
    { title: 'contact', path: '/korisnik/kontakt', icon: 'write' },
    { title: 'credit', path: '/korisnik/kredit', icon: 'credit', count: 'credit' },
    { title: 'offer', path: '/korisnik/oglasavanje', icon: 'chart' },
];

// a zero says nothing, so the count is left out
export const shownCount = (item, counts) => {
    const value = item.count ? counts[item.count] : undefined;
    return value === 0 || value === '0' ? undefined : value;
};

const HELP_LINKS = [
    { title: 'help_info', path: '/uputstvo' },
    { title: 'help_sellers', path: '/uputstvo-prodavci' },
    { title: 'help_tenants', path: '/uputstvo-zakupci' },
    { title: 'help_ads', path: '/upustvo-oglasavanje' },
];

const STATISTICS_PATH = '/korisnik/statistika/';

export const UserPanel = () => {
    const location = useLocation();
    const { t } = useTranslation(["profile", "listing"]);
    const logout = useLogout();
    const counts = useCounts();
    // pages render their header buttons into this element
    const [actionsSlot, setActionsSlot] = useState(null);

    const path = location.pathname;
    const isStatistics = path.startsWith(STATISTICS_PATH);
    const current = isStatistics
        ? PANEL_MENU.find(item => item.title === 'active')
        : PANEL_MENU.find(item => item.path === path) || PANEL_MENU[0];

    return (
        <div className='hs-panel'>
            <aside className='hs hs-panel-side'>
                <div className='hs-panel-menu'>
                    <p className='hs-panel-menu-title'>{t('panel')}</p>
                    {PANEL_MENU.map(item => {
                        const count = shownCount(item, counts);
                        return (
                            <Link key={item.path} to={item.path} onClick={() => sessionStorage.removeItem('currentPage')}
                                className={`hs-panel-menu-item ${item === current ? 'on' : ''}`}>
                                <span className='hs-panel-menu-icon'><PanelIcon name={item.icon} /></span>
                                <span className='hs-panel-menu-label'>{t(item.title)}</span>
                                {count !== undefined &&
                                    <span className={`hs-panel-menu-count ${item.alert ? 'alert' : ''}`}>{count}</span>}
                            </Link>
                        );
                    })}
                    <div className='hs-panel-menu-divider'></div>
                    <button className='hs-panel-menu-item logout' onClick={logout}>
                        <span className='hs-panel-menu-icon'><PanelIcon name='logout' /></span>
                        {t('logout')}
                    </button>
                </div>
                <div className='hs-panel-help'>
                    <p className='hs-panel-help-title'>{t('help')}</p>
                    {HELP_LINKS.map(link => (
                        <Link key={link.path} to={link.path}>{t(link.title)}<span>→</span></Link>
                    ))}
                </div>
            </aside>

            <div className='hs-panel-main'>
                <div className='hs hs-panel-head'>
                    <div>
                        <p className='hs-panel-eyebrow'>{t('panel')}</p>
                        <h1>{isStatistics ? t('statistics', { ns: 'listing' }) : t(current.title)}</h1>
                    </div>
                    <div className='hs-panel-actions' ref={setActionsSlot}></div>
                </div>
                <Outlet context={{ actionsSlot, refreshCounts: counts.refresh }} />
            </div>
        </div>
    );
};
